/**
 * Bundle 71 - Registrations filter overhaul.
 *
 * Renders a Sheet-based drawer with 7 workflow-driven filter groups.
 * The drawer emits a single `filters` object; the parent page turns
 * that into query params + URL state.
 *
 * The 7 groups (per Bundle 70 Task 5 spec):
 *   A - Payment
 *   B - Profile completeness
 *   C - Vetting / admin action
 *   D - Attendee attributes
 *   E - Communication state
 *   F - Time / cohort
 *   G - Group / relationship
 */
import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
  SheetFooter,
} from '@/components/ui/sheet';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronRight, Filter, X } from 'lucide-react';

const _emptyFilters = {
  // A
  payment_status: '', payment_method: '', amount_min: '', amount_max: '',
  paid_within_days: '', refund_status: '',
  // B
  photo_status: '', social_status: '', dob_present: '', phone_present: '',
  age_range: '', gender: '',
  // C
  vetting_status: '', ticket_status: '', needs_follow_up: '',
  // D
  city: '', state: '', signup_source: '',
  prior_events_min: '', prior_events_max: '', is_new_attendee: '',
  // E
  received_nudge: '', opened_nudge: '', clicked_nudge: '',
  received_gate_reminder: '',
  // F
  signed_up_within_days: '', rsvped_within_days: '',
  signed_up_from: '', signed_up_to: '', sort_by: '', sort_dir: '',
  // G
  party_size: '', referred_by_user_id: '', referral_count: '',
};

export const EMPTY_FILTERS = _emptyFilters;

const _countActive = (filters) => {
  return Object.entries(filters || {}).reduce((n, [k, v]) => {
    if (v === '' || v === null || v === undefined) return n;
    return n + 1;
  }, 0);
};

const GroupHeader = ({ title, groupKey, open, toggle, activeCount }) => (
  <button
    type="button"
    onClick={() => toggle(groupKey)}
    className="w-full flex items-center justify-between py-2 border-b border-neutral-200 hover:bg-neutral-50 transition-colors"
    data-testid={`filter-group-${groupKey}-toggle`}
  >
    <div className="inline-flex items-center gap-2">
      {open ? (
        <ChevronDown className="h-3 w-3" />
      ) : (
        <ChevronRight className="h-3 w-3" />
      )}
      <span className="text-[11px] uppercase tracking-widest font-semibold text-neutral-700">
        {title}
      </span>
      {activeCount > 0 ? (
        <Badge
          variant="outline"
          className="h-4 text-[9px] px-1 bg-secondary-50 text-secondary-800 border-secondary-200"
        >
          {activeCount}
        </Badge>
      ) : null}
    </div>
  </button>
);

const _selectRow = (label, key, value, options, onChange) => (
  <div className="grid grid-cols-[110px_1fr] items-center gap-2 py-1">
    <Label className="text-[11px] text-neutral-600">{label}</Label>
    <Select value={value || 'any'} onValueChange={(v) => onChange(key, v === 'any' ? '' : v)}>
      <SelectTrigger
        className="h-8 text-xs"
        data-testid={`filter-${key}-select`}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="any">any</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const _inputRow = (label, key, value, placeholder, onChange, testType = 'text') => (
  <div className="grid grid-cols-[110px_1fr] items-center gap-2 py-1">
    <Label className="text-[11px] text-neutral-600">{label}</Label>
    <Input
      value={value || ''} placeholder={placeholder} className="h-8 text-xs"
      onChange={(e) => onChange(key, e.target.value)}
      data-testid={`filter-${key}-input`}
      type={testType}
    />
  </div>
);

const RegistrationsFilterDrawer = ({ value, onChange, onApply, onReset }) => {
  const [open, setOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState(new Set(['A', 'B']));
  const toggleGroup = (k) => setOpenGroups((prev) => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });
  const activeCount = useMemo(() => _countActive(value), [value]);
  const set = (k, v) => onChange({ ...(value || {}), [k]: v });

  const groupCounts = useMemo(() => ({
    A: ['payment_status', 'payment_method', 'amount_min', 'amount_max', 'paid_within_days', 'refund_status'].filter((k) => (value || {})[k]).length,
    B: ['photo_status', 'social_status', 'dob_present', 'phone_present', 'age_range', 'gender'].filter((k) => (value || {})[k]).length,
    C: ['vetting_status', 'ticket_status', 'needs_follow_up'].filter((k) => (value || {})[k]).length,
    D: ['city', 'state', 'signup_source', 'prior_events_min', 'prior_events_max', 'is_new_attendee'].filter((k) => (value || {})[k]).length,
    E: ['received_nudge', 'opened_nudge', 'clicked_nudge', 'received_gate_reminder'].filter((k) => (value || {})[k]).length,
    F: ['signed_up_within_days', 'rsvped_within_days', 'signed_up_from', 'signed_up_to', 'sort_by', 'sort_dir'].filter((k) => (value || {})[k]).length,
    G: ['party_size', 'referred_by_user_id', 'referral_count'].filter((k) => (value || {})[k]).length,
  }), [value]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="outline" size="sm"
          className="inline-flex items-center gap-1.5"
          data-testid="filters-drawer-open-btn"
        >
          <Filter className="h-3.5 w-3.5" /> Filters
          {activeCount > 0 ? (
            <Badge
              variant="outline"
              className="ml-1 h-4 text-[10px] px-1 bg-primary-50 text-primary-800 border-primary-200"
              data-testid="filters-active-count"
            >
              {activeCount}
            </Badge>
          ) : null}
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right" className="w-[420px] sm:w-[500px] overflow-y-auto"
        data-testid="filters-drawer"
      >
        <SheetHeader>
          <SheetTitle className="inline-flex items-center gap-2">
            <Filter className="h-4 w-4" /> Filter registrations
            {activeCount > 0 ? (
              <span className="text-[10px] text-neutral-500 font-normal">
                {activeCount} active
              </span>
            ) : null}
          </SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-2">
          {/* GROUP A - Payment */}
          <div>
            <GroupHeader
              title="Payment" groupKey="A" open={openGroups.has('A')}
              toggle={toggleGroup} activeCount={groupCounts.A}
            />
            {openGroups.has('A') ? (
              <div className="pl-2 pt-2 space-y-1">
                {_selectRow('Status', 'payment_status', value.payment_status, [
                  { value: 'not_required', label: 'not required' },
                  { value: 'pending', label: 'pending' },
                  { value: 'user_marked_paid', label: 'user marked paid' },
                  { value: 'auto_verified_pending_review', label: 'auto-verified pending review' },
                  { value: 'verified', label: 'verified' },
                  { value: 'refunded', label: 'refunded' },
                  { value: 'waived', label: 'waived' },
                  { value: 'failed', label: 'failed' },
                ], set)}
                {_selectRow('Method', 'payment_method', value.payment_method, [
                  { value: 'stripe', label: 'stripe' },
                  { value: 'zelle', label: 'zelle' },
                  { value: 'venmo', label: 'venmo' },
                  { value: 'paypal', label: 'paypal' },
                  { value: 'manual', label: 'manual' },
                ], set)}
                {_inputRow('Amount min', 'amount_min', value.amount_min, 'cents (e.g. 2500)', set, 'number')}
                {_inputRow('Amount max', 'amount_max', value.amount_max, 'cents', set, 'number')}
                {_selectRow('Paid within', 'paid_within_days', value.paid_within_days, [
                  { value: '1', label: 'last 24h' },
                  { value: '7', label: 'last 7 days' },
                  { value: '30', label: 'last 30 days' },
                ], set)}
                {_selectRow('Refund', 'refund_status', value.refund_status, [
                  { value: 'none', label: 'no refund' },
                  { value: 'requested', label: 'requested' },
                  { value: 'refunded', label: 'refunded' },
                ], set)}
              </div>
            ) : null}
          </div>

          {/* GROUP B - Profile */}
          <div>
            <GroupHeader
              title="Profile completeness" groupKey="B" open={openGroups.has('B')}
              toggle={toggleGroup} activeCount={groupCounts.B}
            />
            {openGroups.has('B') ? (
              <div className="pl-2 pt-2 space-y-1">
                {_selectRow('Photo', 'photo_status', value.photo_status, [
                  { value: 'has_photo', label: 'has photo' },
                  { value: 'needs_photo', label: 'needs photo' },
                  { value: 'multi_photo', label: 'has multiple' },
                ], set)}
                {_selectRow('Social', 'social_status', value.social_status, [
                  { value: 'has_social', label: 'has social' },
                  { value: 'needs_social', label: 'needs social' },
                ], set)}
                {_selectRow('DOB', 'dob_present', value.dob_present, [
                  { value: 'yes', label: 'yes' }, { value: 'no', label: 'no' },
                ], set)}
                {_selectRow('Phone', 'phone_present', value.phone_present, [
                  { value: 'yes', label: 'yes' }, { value: 'no', label: 'no' },
                ], set)}
                {_selectRow('Age', 'age_range', value.age_range, [
                  { value: '21-29', label: '21-29' },
                  { value: '30-39', label: '30-39' },
                  { value: '40-49', label: '40-49' },
                  { value: '50+', label: '50+' },
                ], set)}
                {_selectRow('Gender', 'gender', value.gender, [
                  { value: 'male', label: 'male' },
                  { value: 'female', label: 'female' },
                  { value: 'nonbinary', label: 'nonbinary' },
                  { value: 'prefer_not', label: 'prefer not' },
                ], set)}
              </div>
            ) : null}
          </div>

          {/* GROUP C - Vetting */}
          <div>
            <GroupHeader
              title="Vetting" groupKey="C" open={openGroups.has('C')}
              toggle={toggleGroup} activeCount={groupCounts.C}
            />
            {openGroups.has('C') ? (
              <div className="pl-2 pt-2 space-y-1">
                {_selectRow('Vetted', 'vetting_status', value.vetting_status, [
                  { value: 'not_reviewed', label: 'not reviewed' },
                  { value: 'approved', label: 'approved' },
                  { value: 'flagged', label: 'flagged' },
                  { value: 'rejected', label: 'rejected' },
                ], set)}
                {_selectRow('Ticket', 'ticket_status', value.ticket_status, [
                  { value: 'not_issued', label: 'not issued' },
                  { value: 'issued', label: 'issued' },
                  { value: 'revoked', label: 'revoked' },
                ], set)}
                {_selectRow('Follow-up', 'needs_follow_up', value.needs_follow_up, [
                  { value: 'yes', label: 'needs follow-up' },
                  { value: 'no', label: 'no follow-up' },
                ], set)}
              </div>
            ) : null}
          </div>

          {/* GROUP D - Attributes */}
          <div>
            <GroupHeader
              title="Attributes" groupKey="D" open={openGroups.has('D')}
              toggle={toggleGroup} activeCount={groupCounts.D}
            />
            {openGroups.has('D') ? (
              <div className="pl-2 pt-2 space-y-1">
                {_inputRow('City', 'city', value.city, 'e.g. Austin', set)}
                {_inputRow('State', 'state', value.state, 'e.g. TX', set)}
                {_selectRow('Sign-up src', 'signup_source', value.signup_source, [
                  { value: 'organic', label: 'organic' },
                  { value: 'ad', label: 'ad' },
                  { value: 'referral', label: 'referral' },
                  { value: 'magic-link', label: 'magic link' },
                  { value: 'google', label: 'google' },
                ], set)}
                {_inputRow('Prior min', 'prior_events_min', value.prior_events_min, '0', set, 'number')}
                {_inputRow('Prior max', 'prior_events_max', value.prior_events_max, '10', set, 'number')}
                {_selectRow('New attendee', 'is_new_attendee', value.is_new_attendee, [
                  { value: 'yes', label: 'new (0 prior)' },
                  { value: 'no', label: 'returning' },
                ], set)}
              </div>
            ) : null}
          </div>

          {/* GROUP E - Communication */}
          <div>
            <GroupHeader
              title="Communication" groupKey="E" open={openGroups.has('E')}
              toggle={toggleGroup} activeCount={groupCounts.E}
            />
            {openGroups.has('E') ? (
              <div className="pl-2 pt-2 space-y-1">
                {_selectRow('Nudge sent', 'received_nudge', value.received_nudge, [
                  { value: 'yes', label: 'yes' }, { value: 'no', label: 'never' },
                ], set)}
                {_selectRow('Nudge opened', 'opened_nudge', value.opened_nudge, [
                  { value: 'yes', label: 'yes' },
                ], set)}
                {_selectRow('Nudge clicked', 'clicked_nudge', value.clicked_nudge, [
                  { value: 'yes', label: 'yes' },
                ], set)}
                {_selectRow('Gate reminder', 'received_gate_reminder', value.received_gate_reminder, [
                  { value: 'yes', label: 'yes' },
                ], set)}
              </div>
            ) : null}
          </div>

          {/* GROUP F - Time */}
          <div>
            <GroupHeader
              title="Time / cohort" groupKey="F" open={openGroups.has('F')}
              toggle={toggleGroup} activeCount={groupCounts.F}
            />
            {openGroups.has('F') ? (
              <div className="pl-2 pt-2 space-y-1">
                {_selectRow('Signed up', 'signed_up_within_days', value.signed_up_within_days, [
                  { value: '1', label: 'last 24h' },
                  { value: '7', label: 'last 7 days' },
                  { value: '30', label: 'last 30 days' },
                ], set)}
                {_selectRow('RSVPed', 'rsvped_within_days', value.rsvped_within_days, [
                  { value: '1', label: 'last 24h' },
                  { value: '7', label: 'last 7 days' },
                  { value: '30', label: 'last 30 days' },
                ], set)}
                {_inputRow('From (ISO)', 'signed_up_from', value.signed_up_from, '2026-02-01', set)}
                {_inputRow('To (ISO)', 'signed_up_to', value.signed_up_to, '2026-02-28', set)}
                {_selectRow('Sort by', 'sort_by', value.sort_by, [
                  { value: 'created_at', label: 'signup date' },
                  { value: 'updated_at', label: 'updated' },
                  { value: 'payment_completed_at', label: 'payment date' },
                  { value: 'name', label: 'name' },
                ], set)}
                {_selectRow('Direction', 'sort_dir', value.sort_dir, [
                  { value: 'desc', label: 'newest first' },
                  { value: 'asc', label: 'oldest first' },
                ], set)}
              </div>
            ) : null}
          </div>

          {/* GROUP G - Relationship */}
          <div>
            <GroupHeader
              title="Group / relationship" groupKey="G" open={openGroups.has('G')}
              toggle={toggleGroup} activeCount={groupCounts.G}
            />
            {openGroups.has('G') ? (
              <div className="pl-2 pt-2 space-y-1">
                {_selectRow('Party size', 'party_size', value.party_size, [
                  { value: 'solo', label: 'solo' },
                  { value: 'plus_one', label: 'plus one' },
                  { value: 'group', label: 'group (3+)' },
                ], set)}
                {_inputRow('Referred by', 'referred_by_user_id', value.referred_by_user_id, 'user id', set)}
                {_selectRow('Referral cnt', 'referral_count', value.referral_count, [
                  { value: '0', label: '0 referrals' },
                  { value: '1', label: '1 referral' },
                  { value: '2plus', label: '2+ referrals' },
                ], set)}
              </div>
            ) : null}
          </div>
        </div>
        <SheetFooter className="mt-4 gap-2">
          <Button
            variant="outline" size="sm" onClick={() => { onReset(); }}
            data-testid="filters-reset-btn"
          >
            <X className="h-3.5 w-3.5 mr-1" /> Reset
          </Button>
          <Button
            size="sm" onClick={() => { onApply(); setOpen(false); }}
            data-testid="filters-apply-btn"
          >
            Apply
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default RegistrationsFilterDrawer;
